import {Divider, Grid, Stack, styled, TextField, useMediaQuery} from '@mui/material'
import {addDays, format} from 'date-fns'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {RequestType} from '../../models'
import {
  hideModal,
  SetShowModalPayload,
  showModalDialog,
} from '../../redux/reducers/appSettingsReducer'
import {showErrorMessage, showSuccessMessage} from '../../redux/reducers/snackbarReducer'
import {RootStore, useAppDispatch} from '../../redux/store'
import {employeeService} from '../../services/employeeService'
import {requestsService} from '../../services/requestsService'
import Card from '../../shared/layout/Card'
import Alert from '../../shared/UI/Alert/Alert'
import CardTitle from '../../shared/UI/CardTitle'
import InstanceStatusBanner from '../../shared/UI/InstanceStatusBanner'
import LoadingIndicator from '../../shared/UI/LoadingIndicator'
import Modal from '../../shared/UI/Modal'
import MyActionsPageHeader from '../../shared/UI/MyActionsPageHeader'
import NoDataFound from '../../shared/UI/NoDataFound'
import Paragraph from '../../shared/UI/Paragraph'
import TimePicker from '../../shared/UI/TimePicker'
import {isMobileDown} from '../../theme/deviceChecks'
import {BaseResponse} from '../../types/base-response'
import {EntitlementSplit} from '../../types/entitlement-split'
import {
  RequestDayRequest,
  RequestDetailResponse,
  RequestOverlapsResponse,
  RequestUpdateRequest,
} from '../../types/requests'
import {
  formatDateTimeWithLocale,
  formatDateWithLocale,
  formatDateWithTimeZoneStr,
  formatTime,
  getLocalDateString,
} from '../../utils/date-utils'
import UserErrorMessage from '../../utils/errorFilter'
import {CheckboxTextfield} from '../Stacks'
import WallChart from '../WallChart'
import { WallChartSegments, WallChartSelectedTimeSpan, WallChartTimespans } from '../WallChart/WallChartTimespanSelector/types'
import { OccurrenceRequestProps, RequestAction, RequestStatus } from './types'
import OccurenceRequestWarnings from '../MyRequests/OccurrenceRequestWarnings'
import {
  calculateMaxHeight,
  calculateStatusLabel,
  canSetAmended,
  useNonExistentRequestHandler,
  useRequestChecker,
  useRequestDetailFetcher,
} from './utils'
import CardTitleWithLink from '../../shared/UI/CardTitleWithLink/CardTitleWithLink'
import Drawer from '../../shared/UI/Drawer'
import ChangedRequestDays from './ChangedRequestDays'
import OccurenceRequestSummaryCard from './OccurenceRequestSummaryCard'
import {globalContent} from '../../utils/constants'
import ActionedInfo from '../../shared/UI/ActionedInfo/ActionedInfo'
import CommentSection from '../../shared/UI/AddComment/CommentSection'
import ActionButtons from '../../shared/UI/ActionButtons/ActionButtons'

export const StyledGrid = styled(Grid)`
    &::-webkit-scrollbar {
        width: 17px
    }

    &::-webkit-scrollbar-thumb {
        border: 5px solid transparent;
        background-clip: content-box;
    }
`

export interface FieldTouch {
  reviewerComments: boolean
  rows: boolean
}

function Occurrence({occurrenceType}: OccurrenceRequestProps) {
  const {id: requestId} = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [requestDetail, setRequestDetail] = useState<RequestDetailResponse | null>(null)
  const [initialRequestDetail, setInitialRequestDetail] = useState<RequestDetailResponse | null>(
    null
  )
  const [employeeId, setEmployeeId] = useState<number>()
  const [birthDay, setBirthDay] = useState<number>()
  const [birthMonth, setBirthMonth] = useState<number>()
  const [isBirthday, setIsBirthday] = useState<boolean>(false)
  const [hasChanged, setHasChanged] = useState<boolean>(false)
  const [entitlementSplit, setEntitlementSplit] = useState<EntitlementSplit>()
  const [wfhEntitlementSplit, setWfhEntitlementSplit] = useState<EntitlementSplit>()
  const [isWfhOccurrence, setIsWfhOccurrence] = useState<boolean>(false)
  const [fieldsTouched, setFieldsTouched] = useState<FieldTouch>({
    reviewerComments: false,
    rows: false,
  })
  const [rowRequestDayId, setRowRequestDayId] = useState<number[]>([])
  const [formattedShiftStartTime, setFormattedShiftStartTime] = useState<Date>()
  const [declineValidationErrors, setDeclineValidationErrors] = useState<any>({})
  const [requestedHoursAttributes, setRequestedHoursAttributes] = useState<{ title?: string }>({})
  const [requestedHoursNumberAttributes, setRequestedHoursNumberAttributes] = useState<{
    title: string | number | undefined | any
  }>({title: undefined})
  const [isDeclinedOrCancelled, setIsDeclinedOrCancelled] = useState<boolean>(true)
  const [previousYear, setPreviousYear] = useState<boolean>(false)
  const [requestIsLocked, setRequestIsLocked] = useState<boolean>(true)
  const [approveLoading, setApproveLoading] = useState<boolean>(false)
  const [declineLoading, setDeclineLoading] = useState<boolean>(false)
  const [negativeHoursApproval, setNegativeHoursApproval] = useState<boolean>(false)
  const [boxChecked, setBoxChecked] = useState<RequestDayRequest[]>([])
  const [requestOverlaps, setRequestOverlaps] = useState<RequestOverlapsResponse>()
  const [showReviewerComments, setShowReviewerComments] = useState<boolean>(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [commentError, setCommentError] = React.useState(false)

  const bankHolidays = useSelector<RootStore, any[] | null>(
    (state: RootStore) => state.appSettings.bankHolidays
  )
  const {currentEntitlementPeriodResponse} = useSelector((state: RootStore) => state.appSettings)
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)
  const [showCommentsOnError] = React.useState(false)
  const isChangeRequested = () => {
    switch (requestDetail?.requestTypeId) {
      case RequestType.HOLIDAY:
      case RequestType.HOLIDAY_M:
      case RequestType.WORK_FROM_HOME:
        return requestDetail.requestStatusId === 2 && !requestDetail.isCancellation

      default:
        return false
    }
  }

  const checkIfRequestExists = useRequestChecker()
  const handleNonExistentRequest = useNonExistentRequestHandler()
  const fetchRequestDetail = useRequestDetailFetcher()
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const isActionedInfoVisible = requestDetail?.actionedBy && requestDetail?.actionedDateTime

  const maxHeight = calculateMaxHeight(isActionedInfoVisible, showReviewerComments, isChangeRequested())

  const calculateHours = useCallback(() => {
    let total = 0
    requestDetail?.requestDays.forEach(day => {
      total += day.checked ? Number(day.hours) : 0
    })
    return total
  }, [requestDetail?.requestDays])

  useEffect(() => {
    if (!employeeId) {
      return
    }

    const isBirthDay = occurrenceType === 'Birthday'
    if (isBirthDay) {
      employeeService.getEmployeeDepartment(employeeId).then(res => {
        setBirthDay(res.employee.birthDay)
        setBirthMonth(res.employee.birthMonth)
      })
    }

    setIsBirthday(isBirthDay)
  }, [employeeId, occurrenceType])

  useEffect(() => {
    if (!requestDetail) {
      return
    }

    setBoxChecked(
      requestDetail.requestDays.filter(
        day => day.checked && (day.hours === null || day.hours === '0' || day.hours === '')
      )
    )
    if (requestDetail.dateFrom && requestDetail.shiftStartTime) {
      const dateTimeString = `${format(
        new Date(requestDetail.dateFrom),
        'yyyy-MM-dd hh-mm-ss'
      ).slice(0, 11)}${requestDetail.shiftStartTime!}`
      setFormattedShiftStartTime(new Date(dateTimeString))
    }
    setRequestedHoursAttributes(() => {
      if (!requestDetail.shiftStartTime && !requestDetail.totalRequestedHours) {
        return {
          title: 'Request',
        }
      }
      return {
        title: requestDetail.requestTypeId === 3 ? 'Shift Start Time' : 'Requested Hours',
      }
    })
    setRequestedHoursNumberAttributes(() => {
      if (!requestDetail.shiftStartTime && !requestDetail.totalRequestedHours) {
        return {
          title: undefined,
        }
      }
      return {
        title: calculateHours().toFixed(2),
      }
    })
    setIsDeclinedOrCancelled(
      requestDetail.requestStatusId === 5 || requestDetail.requestStatusId === 7
    )
  }, [requestDetail, employeeId, calculateHours])

  const mobile = useMediaQuery(isMobileDown)

  const activeRoute = useSelector<RootStore, string>(
    (state: RootStore) => state.appSettings.activeRoute
  )

  const isFormValid = (isDecline: boolean) => {
    setDeclineValidationErrors({})
    const declineErrors: any = {}

    if (hasChanged && requestDetail?.reviewerComments === '') {
      return false
    }

    if (isDecline && requestDetail?.reviewerComments === '') {
      declineErrors.reviewerComments = true
    }

    if (Object.keys(declineErrors).length !== 0) {
      setDeclineValidationErrors(declineErrors)
      return false
    }

    if (boxChecked && boxChecked.length > 0) {
      boxChecked.map(requestDay =>
        setRowRequestDayId([...rowRequestDayId, requestDay.requestDayId])
      )

      return false
    }

    return true
  }

  const {showModal, title, message, type, buttonLabel} = useSelector<
    RootStore,
    SetShowModalPayload
  >((state: RootStore) => state.appSettings.modalProps)

  const wallChart = useMemo(() => {
    if (!requestDetail) {
      return null
    }

    const filterPropertiesOverride = {
      dateFrom: formatDateWithTimeZoneStr(requestDetail.dateFrom),
      dateTo: formatDateWithTimeZoneStr(
        addDays(new Date(requestDetail.dateFrom), 13)
      ),
      directReportees: true,
      departmentTeamIds: [],
      employees: [],
      includeThresholds: true,
      thresholdsForEmployee: requestDetail.employeeId,
    }

    const timeSpanPropertiesOverride: WallChartSelectedTimeSpan = {
      startDate: new Date(requestDetail.dateFrom),
      endDate: addDays(new Date(requestDetail.dateFrom), 13),
      directReport: true,
      segments: {
        masterSegments: 14,
        divisions: WallChartSegments.SEGMENT_DIV_TWOWEEK,
        timespan: WallChartTimespans.TWOWEEK,
      },
      title: '',
      rangeType: 'TwoWeek',
    }

    return <WallChart
      title="Who's Away"
      isWallChartView={false}
      filterPropertiesOverride={filterPropertiesOverride}
      timeSpanPropertiesOverride={timeSpanPropertiesOverride}
    />
  }, [requestDetail?.dateFrom, requestDetail?.employeeId])

  const navigate = useNavigate()
  const location = useLocation()

  const displayClashModal = () => {
    dispatch(
      showModalDialog({
        title: 'Are you sure?',
        message: `Are you sure you would like to decline this request?`,
        buttonLabel: 'Decline',
        type: 'question',
        showModal: true,
      })
    )
  }

  const requestDetailHasChanged = useCallback((): boolean => {
    if (requestDetail && initialRequestDetail) {
      return Object.keys(requestDetail).some(key => {
        if (key !== 'reviewerComments' || requestDetail.requestStatusId === RequestStatus.AMENDED) {
          return (
            requestDetail[key as keyof RequestDetailResponse] !==
            initialRequestDetail[key as keyof RequestDetailResponse]
          )
        }
        return false
      })
    }
    return false
  }, [initialRequestDetail, requestDetail])

  useEffect(() => {
    setHasChanged(requestDetailHasChanged())
  }, [requestDetailHasChanged])

  const transformBody = (requestAction: number): RequestUpdateRequest | undefined => {
    if (!requestDetail) {
      return
    }

    const requestDays =
      !requestDetail.isCancellation &&
      requestDetail.requestStatusId === 2 &&
      requestAction === RequestAction.APPROVE &&
      requestDetail.requestDaysCr
        ? requestDetail.requestDaysCr
        : requestDetail.requestDays

    return {
      requestId: requestDetail.requestId,
      requestStatusId: requestDetail.requestStatusId,
      requestTypeId: requestDetail.requestTypeId,
      manualTypeId: requestDetail.manualTypeId,
      employeeId: requestDetail.employeeId,
      applyChangesToRota: false,
      departmentId: requestDetail.departmentId,
      reviewerComments: requestDetail.reviewerComments,
      shiftStartTimeStr: requestDetail.shiftStartTime,
      days: requestDays.map(day => ({
        requestDayId: day.requestDayId,
        requestDayDate: day.day,
        hours: Number(day.hours) < 0 ? 0 : Number(day.hours),
        checked: day.checked,
      })),
      dateFrom: format(new Date(requestDetail.dateFrom.toString()), 'yyyy-MM-dd'),
      dateTo: format(new Date(requestDetail.dateTo.toString()), 'yyyy-MM-dd'),
      requestActionId: requestAction,
      isCancellation: requestDetail.isCancellation,
      hasBeenRejected: true,
      conflictOverride: true,
    }
  }

  const handleRequestData = (data: RequestDetailResponse) => {
    setRequestDetail(data)
    setEmployeeId(data.employeeId)
    setInitialRequestDetail(data)
    setEntitlementSplit({
      general: {
        hours: data?.summary.totalHours,
        summerHours: data?.summary.totalSummerHours,
        winterHours: data?.summary.totalWinterHours,
      },
      remaining: {
        hours: data?.summary.remainingHours,
        summerHours: data?.summary.remainingSummerHours,
        winterHours: data?.summary.remainingWinterHours,
      },
      requested: {
        hours: data?.summary.requestedHours,
        summerHours: data?.summary.requestedSummerHours,
        winterHours: data?.summary.requestedWinterHours,
      },
      changeRequests: {
        hours: data?.summary.changeRequestHours,
        summerHours: data?.summary.changeRequestHoursSummer,
        winterHours: data?.summary.changeRequestHoursWinter,
      },
      ifApproved: {
        hours: data?.summary.ifApprovedHours,
        summerHours: data?.summary.ifApprovedHoursSummer,
        winterHours: data?.summary.ifApprovedHoursWinter,
      },
    })
    setWfhEntitlementSplit({
      general: {
        hours: data?.summary.wfhAllowance,
      },
      remaining: {
        hours: data?.summary.remainingWfhHours,
      },
      requested: {
        hours: data?.summary.requestedWfhHours,
      },
      changeRequests: {
        hours: data?.summary.changeRequestedWfhHours,
      },
      ifApproved: {
        hours: data?.summary.ifApprovedWfhHours,
      },
    })
    setIsWfhOccurrence(window.location.href.includes('workfromhomerequest'))
    setPreviousYear(
      currentEntitlementPeriodResponse?.entitlementPeriod.toLowerCase() === 'financialyear'
        ? new Date(data.dateFrom).getFullYear() < currentEntitlementPeriodResponse.year
        : new Date(data.dateFrom).getFullYear() < new Date().getFullYear()
    )
    setRequestIsLocked(
      data?.lockStatus.isLocked &&
      data?.lockStatus.lockedByUser?.trim().toLowerCase() !== userInfo?.name?.trim().toLowerCase()
    )
    setRequestOverlaps(data.requestOverlaps)
  }

  const fetchHolidayRequestDetail = useCallback(async () => {
    setIsLoading(true)
    try {
      const exists = await checkIfRequestExists()
      if (!exists) {
        handleNonExistentRequest()
        return
      }
      const data = await fetchRequestDetail()
      handleRequestData(data)
    } catch (err) {
      const response: BaseResponse = (err as any)?.response?.data
      response?.errors?.forEach(error => {
        dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
      })
      navigate(location.state?.previousLocation || '/myactions')
    } finally {
      setIsLoading(false)
    }
  }, [
    requestId,
    currentEntitlementPeriodResponse?.entitlementPeriod,
    currentEntitlementPeriodResponse?.year,
    userInfo?.name,
    navigate,
    activeRoute,
    dispatch,
  ])

  const requestTypeHasMultiDays = useMemo(() => {
    if (requestDetail) {
      if (requestDetail.requestStatusId === 4) {
        // Manual request
        return requestDetail.manualTypeId !== 1
      }

      switch (requestDetail.requestTypeId) {
        case 1:
        case 5:
        case 6:
          return true
        default:
          return false
      }
    }
  }, [requestDetail])

  const approveRequest = async () => {
    if (isFormValid(false)) {
      setIsLoading(true)
      const body = transformBody(RequestAction.APPROVE)
      if (!body) {
        return
      }

      if (!requestTypeHasMultiDays || body.days.some(day => day.checked)) {
        if (body.isCancellation) {
          body.requestStatusId = RequestStatus.CR_CANCELLED
        } else if (hasChanged && canSetAmended(requestDetail)) {
          body.requestStatusId = RequestStatus.AMENDED
        } else {
          body.requestStatusId = RequestStatus.APPROVED
        }
        setApproveLoading(true)
        body.hasBeenRejected = false
        await requestsService
          .updateHolidayRequest(body)
          .then(response => {
            setApproveLoading(false)
            if (response.status === 200 && response.errors.length > 0) {
              response.errors.forEach(error => {
                dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
              })
            } else if (occurrenceType === 'Birthday' && body.isCancellation) {
              setIsLoading(false)
              dispatch(showSuccessMessage(`${occurrenceType} cancellation approved`))
              navigate(activeRoute || '/myactions')
            } else {
              setIsLoading(false)
              dispatch(showSuccessMessage(`${occurrenceType} request approved`))
              fetchHolidayRequestDetail()
            }
            setTimeout(() => {
            setIsDrawerOpen(false)
            }, 300)
          })
          .catch(err => {
            setApproveLoading(false)
            const response: BaseResponse = err.response.data
            response.errors.forEach(error => {
              dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
            })
            setIsLoading(false)
          })
      } else {
        dispatch(
          showModalDialog({
            title: 'Cancel request?',
            message:
              'All days for this request have been unselected, which will cancel the request. Are you sure you want to proceed?',
            buttonLabel: 'Confirm',
            type: 'question',
            showModal: true,
          })
        )
        setIsLoading(false)
      }
    } else {
      setFieldsTouched({...fieldsTouched, reviewerComments: true})
    }
  }
  const onApproveWithChanges = () => {
    if (requestDetail?.reviewerComments.trim() === '') {
      setCommentError(true)
      setFieldsTouched({...fieldsTouched, reviewerComments: true})
      setShowReviewerComments(true)
    } else {
      approveRequest()
    }
  }

  const declineRequest = async () => {
    if (isFormValid(true)) {
      setIsLoading(true)

      const body = transformBody(RequestAction.DECLINE)

      if (!body) {
        return
      }

      if (body.requestStatusId === RequestStatus.APPROVED_CR) {
        body.requestStatusId = RequestStatus.APPROVED
      } else {
        body.requestStatusId = RequestStatus.DECLINED
      }
      setDeclineLoading(true)

      await requestsService
        .updateHolidayRequest(body)
        .then(data => {
          setDeclineLoading(false)
          if (data.status === 200 && data.errors.length > 0) {
            data.errors.forEach(error => {
              dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
            })
          } else {
            setIsLoading(false)
            dispatch(hideModal())
            dispatch(showSuccessMessage(`${occurrenceType} request declined`))
            const returnTo = location.state?.previousLocation || '/myactions'
            navigate(returnTo)
          }
        })
        .catch(err => {
          setDeclineLoading(false)
          const response: BaseResponse = err.response.data
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
          setIsLoading(false)
        })
    } else {
      setFieldsTouched({...fieldsTouched, reviewerComments: true})
    }
  }

  const getRequestDayCrHours = (requestDayId: number) => {
    if (requestDetail?.requestDaysCr) {
      const requestDayCr: RequestDayRequest | undefined = requestDetail!.requestDaysCr.find(
        (x: RequestDayRequest) => x.requestDayId === requestDayId
      )
      return requestDayCr?.hours || 0
    }
  }

  const calculateRequestDayCrHours = () => {
    if (!requestDetail?.requestDaysCr) {
      return 0
    }
    let total = 0
    requestDetail?.requestDaysCr.forEach(day => {
      total += day.checked ? Number(day.hours) : 0
    })
    return total
  }

  useEffect(() => {
    fetchHolidayRequestDetail()
  }, [fetchHolidayRequestDetail])

  const formattedDateFrom = requestDetail && formatDateWithLocale(requestDetail.dateFrom)
  const formattedDateTo = requestDetail && formatDateWithLocale(requestDetail.dateTo)
  const formattedActionDate =
    requestDetail?.actionedBy && formatDateWithLocale(requestDetail!.actionedDateTime)
  const formattedDateSubmitted =
    requestDetail && formatDateTimeWithLocale(requestDetail.dateSubmitted)

  const submitDisabled = () => {
    const DISABLED = true
    if (
      requestDetail &&
      hasChanged &&
      (requestDetail.requestStatusId === RequestStatus.APPROVED ||
        requestDetail.requestStatusId === RequestStatus.AMENDED)
    ) {
      return !DISABLED
    }
    if (
      !requestDetail ||
      (fieldsTouched.reviewerComments &&
        requestDetail.reviewerComments === '' &&
        fieldsTouched.rows)
    ) {
      return DISABLED
    }
    if (
      requestDetail.requestStatusId === RequestStatus.AMENDED_CR &&
      requestDetail.isCancellation
    ) {
      return !DISABLED
    }
    return !(
      requestDetail.requestStatusId === RequestStatus.PENDING ||
      requestDetail.requestStatusId === RequestStatus.APPROVED_CR ||
      requestDetail.requestStatusId === RequestStatus.DECLINED
    )
  }
  const declineDisabled = () => {
    if (!requestDetail) {
      return true
    }
    if (
      requestDetail.requestStatusId === RequestStatus.AMENDED_CR &&
      requestDetail.isCancellation
    ) {
      return false
    }
    return !(
      requestDetail.requestStatusId === RequestStatus.PENDING ||
      requestDetail.requestStatusId === RequestStatus.AMENDED ||
      requestDetail.requestStatusId === RequestStatus.APPROVED_CR ||
      requestDetail.requestStatusId === RequestStatus.APPROVED
    )
  }

  const bankHolidaysToDisplay = () => {
    const bankHolidaysDates = bankHolidays?.map(x => getLocalDateString(new Date(x.holidayDate)))
    const holidaysDates = requestDetail!.requestDays?.map(x => getLocalDateString(new Date(x.day)))
    const datesToDisplay = bankHolidaysDates?.filter(x => holidaysDates?.includes(x))
    return datesToDisplay?.join(' & ')
  }

  const hasBankHolidaysToDisplay = () => {
    switch (requestDetail?.requestTypeId) {
      case RequestType.HOLIDAY:
      case RequestType.HOLIDAY_M:
      case RequestType.WORK_FROM_HOME:
      case RequestType.LIEU_DAY:
      case RequestType.LIEU_DAY_M:
      case RequestType.SHIFT: {
        const bankHolidaysDates = bankHolidays?.map(x =>
          getLocalDateString(new Date(x.holidayDate))
        )
        const holidaysDates = requestDetail!.requestDays?.map(x =>
          getLocalDateString(new Date(x.day))
        )
        const datesToDisplay = bankHolidaysDates?.filter(x => holidaysDates?.includes(x))
        return datesToDisplay?.length !== 0
      }
      default:
        return false
    }
  }

  const isNegativeHoursApproval = () => {
    if (!entitlementSplit || !requestDetail) {
      return false
    }

    const requestStatusIdList: number[] = [RequestStatus.PENDING, RequestStatus.APPROVED_CR]

    const remainingHours = entitlementSplit?.remaining.hours ? entitlementSplit?.remaining.hours : 0
    let hours = remainingHours - requestDetail!.totalRequestedHours

    if (requestDetail.requestStatusId === RequestStatus.APPROVED_CR) {
      hours = remainingHours - (calculateRequestDayCrHours() - requestDetail!.totalRequestedHours)

      if (requestDetail.isCancellation) {
        hours = remainingHours + calculateHours()
      }
    }

    return occurrenceType === 'Holiday' &&
      requestStatusIdList.includes(requestDetail.requestStatusId) &&
      hours < 0


  }

  useEffect(() => {
    setNegativeHoursApproval(isNegativeHoursApproval())
  }, [entitlementSplit, requestDetail])

  const isValidDay = () => isDeclinedOrCancelled || requestIsLocked
  const handleAddCommentClick = () => {
    setShowReviewerComments(prevState => !prevState)
  }

  const toggleDrawer = () => {
    if (isDrawerOpen) {
      setIsClosing(true)
      setTimeout(() => {
        setIsDrawerOpen(false)
        setIsClosing(false)
      }, 500) 
    } else {
      setIsDrawerOpen(true)
    }
  }

  return (
    <>
      <MyActionsPageHeader title={`${occurrenceType} Request`} />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          {requestDetail && (
            <>
              <InstanceStatusBanner
                showBackButton
                firstname={requestDetail.statusBanner.forename}
                surname={requestDetail.statusBanner.surname}
                status={
                  requestDetail.isCancellation
                    ? 'Cancellation Requested'
                    : calculateStatusLabel(requestDetail)
                }
                requestId={requestDetail.requestId}
              />
              {previousYear && (
                <Alert severity="warning" message="This request is within a previous year" />
              )}
            </>
          )}
        </Grid>
        {requestDetail && (
          <>
            {requestDetail.lockStatus.isLocked && (
              <Grid item xs={12} sx={{'&.MuiGrid-item': {paddingTop: '16px'}}}>
                <Alert
                  severity="info"
                  message={`The request is currently locked by ${requestDetail.lockStatus.lockedByUser}`}
                />
              </Grid>
            )}
            {/* Column 1 */}
            <Grid item xs={12} lg={8}>
              <Grid container spacing={4}>
                <Grid
                  item
                  xs={12}
                  xl={6}
                  style={{
                    display: 'flex',
                    flexFlow: 'column',
                    flex: '1 1 auto',
                    paddingTop: mobile ? '16px' : '32px',
                  }}
                >
                  <Card
                    {...requestedHoursAttributes}
                    icon={
                      requestDetail.requestTypeId === 3 ? (
                        <TimePicker
                          value={formattedShiftStartTime!}
                          onChange={e => {
                            if (e) {
                              setRequestDetail(current => {
                                const {shiftStartTime, ...rest} = current as RequestDetailResponse
                                return {shiftStartTime: formatTime(e), ...rest}
                              })
                            }
                          }}
                          disabled={isDeclinedOrCancelled}
                        />
                      ) : (
                        <CardTitle {...requestedHoursNumberAttributes} />
                      )
                    }
                  >
                    <Grid container spacing={4}>
                      <Grid container item xs={12} spacing={1}>
                        <Grid container item columnSpacing={1} columns={14}>
                          <Grid item xs={4}>
                            <Paragraph weight="bold">{globalContent.dateFrom}</Paragraph>
                          </Grid>
                          <Grid item xs={4}>
                            <Paragraph>{formattedDateFrom}</Paragraph>
                          </Grid>
                          <Grid item xs={3}>
                            <Paragraph weight="bold">
                              {isBirthday ? 'Birth Date' : globalContent.dateTo}
                            </Paragraph>
                          </Grid>
                          <Grid item xs={3}>
                            <Paragraph>
                              {isBirthday ? `${birthDay}/${birthMonth}` : formattedDateTo}
                            </Paragraph>
                          </Grid>
                        </Grid>
                        <Grid container item columnSpacing={1} columns={14}>
                          <Grid item xs={4}>
                            <Paragraph weight="bold">{globalContent.dateSubmitted}</Paragraph>
                          </Grid>
                          <Grid item xs={4}>
                            <Paragraph>{formattedDateSubmitted}</Paragraph>
                          </Grid>
                        </Grid>
                      </Grid>
                      <OccurenceRequestWarnings
                        hasBankHolidaysToDisplay={hasBankHolidaysToDisplay}
                        bankHolidaysDays={bankHolidaysToDisplay}
                        overlapsResponse={requestOverlaps}
                      />
                      <Grid item xs={12}>
                        <TextField
                          label="Requester comments"
                          fullWidth
                          multiline
                          rows={4}
                          disabled
                          defaultValue={requestDetail.requesterComments}
                          value={requestDetail.requesterComments}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
                <Grid
                  item
                  xs={12}
                  xl={6}
                  style={{display: 'flex', flexFlow: 'column', flex: '1 1 auto'}}
                >
                  <OccurenceRequestSummaryCard
                    entitlementSplit={entitlementSplit}
                    wfhEntitlementSplit={wfhEntitlementSplit}
                    isWfhOccurrence={isWfhOccurrence}
                    isLoading={isLoading}
                  />
                </Grid>
                {!mobile && (
                  <Grid item xs={12}>
                    {wallChart}
                  </Grid>
                )}
              </Grid>
            </Grid>
            {/* Column 1 END */}
            {/* Column 2 (Requested Days) */}
            <Grid item xs={12} lg={4}>
              <Card>
                {
                  ['Maternity', 'Paternity', 'Twilight Shift Cover', 'Night Shift Cover', 'Birthday', 'Shift', 'Day Off' ].includes(occurrenceType) ? (
                    <Grid marginBottom={2}>
                      <CardTitle title="Approval" />
                      <Divider />
                    </Grid>
                  ) : (
                    <CardTitleWithLink onClick={toggleDrawer} />
                  )}
                {requestDetail ? (
                  <Grid container spacing={4}>
                    {occurrenceType !== 'Day Off' && occurrenceType !== 'Shift' ? (
                      <>
                        {negativeHoursApproval && (
                          <>
                            <Grid
                              item
                              xs={12}
                              sx={{'&.MuiGrid-item': {paddingTop: '13px', marginBottom: '1px'}}}
                            >
                              <Alert
                                severity="info"
                                message="Action will result in a negative entitlement for this user"
                              />
                            </Grid>
                          </>
                        )}
                        {Boolean(requestDetail.requestDays?.length) && (
                          <StyledGrid
                            item
                            xs={12}
                            sx={{
                              overflowY: 'auto',
                              maxHeight,
                              marginTop: '20px',
                            }}
                          >
                            {isChangeRequested() ? (
                              <ChangedRequestDays
                                requestDays={requestDetail.requestDays}
                                isChangeRequested={isChangeRequested}
                                getRequestDayCrHours={getRequestDayCrHours}
                              />
                            ) : (
                              <Grid container gap={3}>
                                {requestDetail.requestDays?.map(day => (
                                  <>
                                    <Grid item xs={12}>
                                      <CheckboxTextfield
                                        checkboxRequired
                                        day={day}
                                        setRequestDetail={setRequestDetail}
                                        setRowRequestDayId={setRowRequestDayId}
                                        rowRequestDayId={rowRequestDayId}
                                        setFieldsTouched={setFieldsTouched}
                                        isDisabled={isValidDay()}
                                        fieldsTouched={fieldsTouched}
                                      />
                                    </Grid>
                                  </>
                                ))}
                              </Grid>
                            )}
                          </StyledGrid>
                        )}

                        <Grid item xs={12} style={{paddingTop: '0', marginTop: '0'}}>
                          {isChangeRequested() && (
                            <Stack direction="row" marginTop={3}>
                              <TextField
                                label="Change Comments"
                                fullWidth
                                multiline
                                rows={4}
                                disabled
                                defaultValue={requestDetail?.changeComments}
                                sx={{paddingRight: '6px', paddingBottom: '20px'}}
                              />
                            </Stack>
                          )}
                          <CommentSection
                            requestDetail={requestDetail}
                            showReviewerComments={showReviewerComments}
                            handleAddCommentClick={handleAddCommentClick}
                            setRequestDetail={setRequestDetail}
                            setFieldsTouched={setFieldsTouched}
                            hasChanged={hasChanged}
                            isDeclinedOrCancelled={isDeclinedOrCancelled}
                            commentError={commentError}
                            setCommentError={setCommentError}
                            showCommentsOnError={showCommentsOnError}
                          />
                        </Grid>
                      </>
                    ) : (
                      <></>
                    )}
                    {occurrenceType === 'Day Off' || occurrenceType === 'Shift' ? (
                      <Grid item xs={12} marginTop={-3}>
                        <CommentSection
                          requestDetail={requestDetail}
                          showReviewerComments={showReviewerComments}
                          handleAddCommentClick={handleAddCommentClick}
                          setRequestDetail={setRequestDetail}
                          setFieldsTouched={setFieldsTouched}
                          hasChanged={hasChanged}
                          isDeclinedOrCancelled={isDeclinedOrCancelled}
                          commentError={commentError}
                          setCommentError={setCommentError}
                          showCommentsOnError={showCommentsOnError}
                        />
                      </Grid>
                    ) : (
                      <></>
                    )}
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      direction="column"
                      justifyContent="center"
                      gap={1}
                      sx={{paddingRight: '6px'}}
                    >
                      {isChangeRequested() && (
                        <>
                          <Stack direction="row" justifyContent="space-between">
                            <Paragraph weight="bold">Total Approved Hours</Paragraph>
                            <Paragraph>{calculateHours().toFixed(2)}</Paragraph>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Paragraph weight="bold">Total Requested Hours</Paragraph>
                            <Paragraph>{calculateRequestDayCrHours().toFixed(2)}</Paragraph>
                          </Stack>
                        </>
                      )}
                      {requestDetail.actionedBy && (
                        <ActionedInfo
                          actionedBy={requestDetail.actionedBy}
                          formattedActionDate={formattedActionDate}
                        />
                      )}
                    </Grid>
                    {Boolean(boxChecked.length) && (
                      <Grid item xs={12}>
                        <Alert
                          severity="error"
                          message="Please enter a number in the checked field"
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      direction={{xs: 'column', md: 'row'}}
                      sx={{
                        paddingRight: '6px',
                        '&.MuiGrid-item': {
                          paddingTop: '0px',
                          marginTop: '-40px'
                        },
                      }}
                    >
                      <ActionButtons
                        isDeclinedOrCancelled={isDeclinedOrCancelled}
                        requestIsLocked={requestIsLocked}
                        declineDisabled={declineDisabled()}
                        submitDisabled={submitDisabled()}
                        approveLoading={approveLoading}
                        onDecline={() => {
                          if (requestDetail?.reviewerComments.trim() === '') {
                            setCommentError(true)
                            setFieldsTouched({...fieldsTouched, reviewerComments: true})
                            setShowReviewerComments(true)
                          } else {
                            setFieldsTouched({...fieldsTouched, reviewerComments: true})

                            if (isFormValid(true)) {
                              displayClashModal()
                            }
                          }
                        }}
                        onApprove={approveRequest}
                        onApproveWithChanges={onApproveWithChanges}
                        hasChanges={hasChanged}
                        showRejectButton={false}
                        declineLabel='Decline'
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <>
                    <NoDataFound show={!isLoading && !requestDetail} />
                    <LoadingIndicator show={isLoading} />
                  </>
                )}
              </Card>
            </Grid>
            <Drawer
              isOpen={isDrawerOpen}
              onClose={toggleDrawer}
              narrow
              title="All days"
            >
              <Grid
                marginRight={4}
                marginLeft={4}
              >
                <Grid
                  container
                  gap={3}
                  sx={{
                    overflowY: 'auto',
                    maxHeight: showReviewerComments ? 'calc(100vh - 375px)' : 'calc(100vh - 250px)',
                    paddingTop: '10px',
                  }}
                >
                  {isChangeRequested() ? (
                    <ChangedRequestDays
                      requestDays={requestDetail.requestDays}
                      isChangeRequested={isChangeRequested}
                      getRequestDayCrHours={getRequestDayCrHours}
                    />
                  ) : (
                    requestDetail.requestDays?.map(day => (
                      <Grid item xs={12} key={day.requestDayId} sx={{marginRight: '10px'}}>
                        <CheckboxTextfield
                          checkboxRequired
                          day={day}
                          setRequestDetail={setRequestDetail}
                          setRowRequestDayId={setRowRequestDayId}
                          rowRequestDayId={rowRequestDayId}
                          setFieldsTouched={setFieldsTouched}
                          isDisabled={isValidDay()}
                          fieldsTouched={fieldsTouched}
                        />
                      </Grid>
                    ))
                  )}
                </Grid>
                <CommentSection
                  requestDetail={requestDetail}
                  showReviewerComments={showReviewerComments}
                  handleAddCommentClick={handleAddCommentClick}
                  setRequestDetail={setRequestDetail}
                  setFieldsTouched={setFieldsTouched}
                  hasChanged={hasChanged}
                  isDeclinedOrCancelled={isDeclinedOrCancelled}
                  commentError={commentError}
                  setCommentError={setCommentError}
                  showCommentsOnError={showCommentsOnError}
                />
                {/* Approvals Card Content */}
                <ActionButtons
                  isDeclinedOrCancelled={isDeclinedOrCancelled}
                  requestIsLocked={requestIsLocked}
                  declineDisabled={declineDisabled()}
                  submitDisabled={submitDisabled()}
                  approveLoading={approveLoading}
                  onDecline={() => {
                    if (requestDetail?.reviewerComments.trim() === '') {
                      setCommentError(true)
                      setFieldsTouched({...fieldsTouched, reviewerComments: true})
                      setShowReviewerComments(true)
                    } else {
                      setFieldsTouched({...fieldsTouched, reviewerComments: true})

                      if (isFormValid(true)) {
                        displayClashModal()
                      }
                    }
                  }}
                  onApprove={approveRequest}
                  onApproveWithChanges={onApproveWithChanges}
                  hasChanges={hasChanged}
                  showRejectButton={false}
                  declineLabel='Decline'
                />
              </Grid>
            </Drawer>
            {/* Column 2 End */}
          </>
        )}
      </Grid>
      <Modal
        type={type}
        open={showModal}
        onClose={() => {
          dispatch(hideModal())
        }}
        onClick={declineRequest}
        title={title}
        message={message}
        buttonLabel={buttonLabel}
        buttonLoading={declineLoading}
      />
    </>
  )
}

export default Occurrence
